import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Banknote,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  Lock,
  LogOut,
  Target,
  TrendingUp,
  Trophy,
  User,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { cn } from './ui/utils';
import { AuthContext } from '../contexts/auth';
import { UpgradeProModal } from './UpgradeProModal';
import logo from '../assets/logo-new.png';

export function Sidebar({ isOpen = false, onClose, isCollapsed = false, onToggleCollapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { signOut, user } = useContext(AuthContext);

  useEffect(() => {
    if (typeof window === 'undefined' || !isOpen || window.innerWidth >= 768) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const isPro = user?.plano === 'pro';
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const menuItems = [
    {
      path: '/dashboard',
      label: t('dashboard'),
      icon: LayoutDashboard,
    },
    {
      path: '/debits',
      label: t('myExpenses'),
      icon: Wallet,
    },
    {
      path: '/groups',
      label: t('groups'),
      icon: Users,
    },
    {
      path: '/conquistas',
      label: t('achievements'),
      icon: Trophy,
    },
    {
      path: '/investimentos',
      label: t('investments'),
      icon: TrendingUp,
      requiresPro: true,
    },
    ...(user?.incomeMode === 'variable' ? [{
      path: '/income-history',
      label: t('manageMonthlyIncome'),
      icon: Banknote,
    }] : []),
    {
      path: '/simulation',
      label: t('goals'),
      icon: Target,
      requiresPro: true,
    },
    {
      path: '/profile',
      label: t('profile'),
      icon: User,
    },
  ];

  const handleNavigate = (item) => {
    if (item.requiresPro && !isPro) {
      setUpgradeOpen(true);
      return;
    }
    navigate(item.path);
    onClose?.();
  };

  const handleLogout = () => {
    signOut();
    navigate('/');
    onClose?.();
  };

  return (
    <>
      <button
        type="button"
        aria-label={t('closeSidebar')}
        aria-hidden={!isOpen}
        tabIndex={isOpen ? 0 : -1}
        onClick={() => onClose?.()}
        className="app-sidebar-overlay fixed inset-0 z-40 bg-slate-950/45 transition-opacity duration-300"
      />

      <aside
        id="app-sidebar"
        className={cn(
          'app-sidebar fixed left-0 top-0 z-50 flex h-screen max-w-sm flex-col bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white shadow-2xl ease-out'
        )}
      >
        <div className="app-sidebar__close items-center justify-end px-4 pt-4">
          <button
            type="button"
            onClick={() => onClose?.()}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-700/70 bg-slate-800/80 text-slate-200 transition-colors hover:bg-slate-700"
            aria-label={t('closeSidebar')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="app-sidebar__header border-b border-slate-700/50">
          <div className="flex flex-col items-center gap-2">
            <img src={logo} alt="Investe Sempre" className="app-sidebar__logo h-auto" />
            <p className="app-sidebar__brand-copy text-center text-xs text-slate-400">
              {t('brandSubtitle')}
            </p>
          </div>
        </div>

        <nav className="app-sidebar__nav flex-1 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const isLocked = item.requiresPro && !isPro;

            return (
              <button
                type="button"
                key={item.path}
                onClick={() => handleNavigate(item)}
                aria-label={isLocked ? `${item.label} — ${t('proLocked')}` : item.label}
                title={isLocked ? t('proLocked') : item.label}
                className={cn(
                  'app-sidebar__nav-button flex w-full cursor-pointer items-center gap-3 rounded-lg border-0 bg-transparent text-left transition-all duration-200',
                  isActive && !isLocked
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/20'
                    : 'text-slate-300 hover:bg-slate-700/50 hover:text-white',
                  isLocked && 'opacity-60'
                )}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="app-sidebar__label min-w-0 flex-1">{item.label}</span>
                {isLocked && <Lock className="h-4 w-4 shrink-0 text-slate-400" />}
              </button>
            );
          })}
        </nav>

        <div className="app-sidebar__footer mt-auto space-y-3 border-t border-slate-700/50">
          <button
            type="button"
            onClick={() => onToggleCollapsed?.()}
            aria-label={isCollapsed ? t('expandSidebar') : t('collapseSidebar')}
            title={isCollapsed ? t('expandSidebar') : t('collapseSidebar')}
            className="app-sidebar__collapse flex w-full cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent text-slate-300 transition-all duration-200 hover:bg-slate-700/50 hover:text-white"
          >
            {isCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
            <span className="app-sidebar__label">
              {isCollapsed ? t('expandSidebar') : t('collapseSidebar')}
            </span>
          </button>

          <button
            type="button"
            onClick={handleLogout}
            aria-label={t('logout')}
            title={t('logout')}
            className="app-sidebar__logout flex w-full cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent text-slate-300 transition-all duration-200 hover:bg-red-600/20 hover:text-red-400"
          >
            <LogOut className="h-5 w-5" />
            <span className="app-sidebar__label">{t('logout')}</span>
          </button>
        </div>
      </aside>

      <UpgradeProModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </>
  );
}
